/**
 * Hook de auditoría — Fase 8
 *
 * Consulta `audit_log` con filtros server-side y paginación. La RLS deja
 * pasar solo a admin_general (policy `audit_admin_select` del schema
 * inicial).
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import type { AuditFiltros, AuditListResult, AuditLog } from '../types/auditoria';

const DEFAULT_PAGE_SIZE = 50;

export function useAuditoria(filtros: AuditFiltros = {}) {
  return useQuery({
    queryKey: ['auditoria', 'list', filtros],
    queryFn: async (): Promise<AuditListResult> => {
      const page = filtros.page ?? 0;
      const pageSize = filtros.pageSize ?? DEFAULT_PAGE_SIZE;
      const from = page * pageSize;
      const to = from + pageSize - 1;

      let q = supabase
        .from('audit_log')
        .select(
          '*, usuario:usuarios!audit_log_usuario_id_fkey(id, email, nombre, apellido)',
          { count: 'exact' },
        )
        .order('created_at', { ascending: false })
        .range(from, to);

      if (filtros.accion) q = q.eq('accion', filtros.accion);
      if (filtros.entidad) q = q.eq('entidad', filtros.entidad);
      if (filtros.usuarioId) q = q.eq('usuario_id', filtros.usuarioId);
      if (filtros.desde) q = q.gte('created_at', filtros.desde);
      // hasta es exclusive del día siguiente para incluir todo el día
      if (filtros.hasta) {
        const next = new Date(filtros.hasta);
        next.setDate(next.getDate() + 1);
        q = q.lt('created_at', next.toISOString().slice(0, 10));
      }

      const { data, error, count } = await q;
      if (error) throw error;

      return {
        rows: (data ?? []) as unknown as AuditLog[],
        count: count ?? 0,
        page,
        pageSize,
      };
    },
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
}
